import { useEffect, useState } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { Link } from 'react-router-dom';
import { ArrowRight, Banknote, Clock, Loader2, Lock, X } from 'lucide-react';

import { adminFetch } from '@/hooks/admin/useAdminApi';
import { stripePromiseFor } from '@/lib/stripe';
import {
  PAYMENT_AUTHORIZATION_ACK,
  PAYMENT_AUTHORIZATION_CLAUSES,
  PAYMENT_AUTHORIZATION_INTRO,
  PAYMENT_AUTHORIZATION_VERSION,
} from '@/lib/paymentTerms';

/**
 * StripeAddMethodModal — the clean "add a payment method" page on OUR own UI.
 * Mints a SetupIntent (stripe-setup-intent), renders Stripe's Payment Element
 * (card / bank — tokenized in the browser, never touches our servers),
 * confirms it, then persists the saved method (stripe-save-method). No
 * invoices, no history, no autopay — just saving an instrument.
 *
 * ⚠ It opens STRAIGHT ON THE FORM, deliberately. An earlier version opened on
 * a three-way choice; three routes on the first screen is a decision nobody
 * asked to make, when the overwhelming majority just want to put a card in.
 * The alternatives live behind one small line at the bottom — "Paying by check
 * or ACH?" — so they are one click away for the customer they are for, and
 * invisible to everyone else.
 *
 * Behind that line, two routes and what each actually costs:
 *
 *   Send payment myself  — nothing saved, nothing to verify, works today.
 *   Type in bank details — routing + account number, no bank login. ⚠ Stripe
 *     then sends a 0.01 USD deposit carrying a 6-digit code, and the customer
 *     has to find it on their statement 1-2 business days later and enter it.
 *     The method is saved but UNUSABLE until they do (MicrodepositNext).
 *
 * ⚠ **There is no third way to save a bank, and it isn't Stripe being
 * awkward.** To DEBIT someone's bank account, Nacha requires authorization
 * plus account validation, so Stripe's `verification_method` offers exactly
 * `instant` (bank login), `microdeposits`, or `automatic` (instant with
 * microdeposit fallback) — there is no "skip". Verified against Stripe's ACH
 * Direct Debit docs 2026-09-03; don't go looking for a flag that doesn't
 * exist. The no-wait ACH route is the bank sign-in inside the Payment Element
 * on the first screen.
 */

interface SetupIntentResp {
  ok: true;
  client_secret: string;
  publishable_key: string;
}

interface SaveResp {
  ok: true;
  status: 'active' | 'pending_verification';
  verification_url: string | null;
  microdeposits_arrive_on: string | null;
  method: { stripe_pm_id: string };
}

type Mode = 'default' | 'bank_manual';

interface PendingResult {
  verificationUrl: string | null;
  arriveOn: string | null;
}

export function StripeAddMethodModal({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  /** Called only when the method is immediately usable. */
  onSaved: (stripePmId: string) => void;
}) {
  const [mode, setMode] = useState<Mode>('default');
  const [showAlt, setShowAlt] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [publishableKey, setPublishableKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pending, setPending] = useState<PendingResult | null>(null);

  useEffect(() => {
    let cancelled = false;
    setClientSecret(null);
    setLoadError(null);
    (async () => {
      try {
        const resp = await adminFetch<SetupIntentResp>('stripe-setup-intent', {
          method: 'POST',
          body: JSON.stringify({ mode }),
        });
        if (cancelled) return;
        if (!resp.client_secret || !resp.publishable_key) {
          setLoadError('Payment setup is not fully configured yet.');
          return;
        }
        setClientSecret(resp.client_secret);
        setPublishableKey(resp.publishable_key);
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : 'Could not start payment setup.');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mode]);

  const title = pending
    ? 'Almost there — one step left'
    : mode === 'bank_manual'
      ? 'Enter your bank details'
      : 'Add a payment method';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-brix-surface shadow-card-deep overflow-hidden flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-brix-border bg-brix-surface-alt/60">
          <h2 className="text-lg font-bold text-brix-ink flex items-center gap-2">
            <Lock size={18} className="text-brix-primary" /> {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-brix-muted hover:bg-brix-primary-tint hover:text-brix-ink transition-colors"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto">
          {pending ? (
            <MicrodepositNext pending={pending} onClose={onClose} />
          ) : loadError ? (
            <p className="text-sm text-red-700">
              {loadError}
              <br />
              <span className="text-brix-muted">Please try again, or give us a call.</span>
            </p>
          ) : !clientSecret || !publishableKey ? (
            <div className="py-8 flex items-center justify-center text-sm text-brix-muted">
              <Loader2 size={18} className="animate-spin mr-2" /> Preparing secure form…
            </div>
          ) : (
            <Elements
              stripe={stripePromiseFor(publishableKey)}
              options={{ clientSecret, appearance: { theme: 'stripe' } }}
            >
              <SetupForm
                mode={mode}
                onSaved={onSaved}
                onPending={setPending}
                onBack={mode === 'bank_manual' ? () => setMode('default') : onClose}
                backLabel={mode === 'bank_manual' ? 'Back' : 'Cancel'}
              />
            </Elements>
          )}

          {/* The quiet way out, for the customer who does not want us charging
              anything. One line until it's clicked — see the header note. */}
          {!pending && mode === 'default' && (
            <div className="mt-5 border-t border-brix-border pt-4">
              {!showAlt ? (
                <button
                  type="button"
                  onClick={() => setShowAlt(true)}
                  className="text-sm text-brix-muted hover:text-brix-primary transition-colors"
                >
                  Paying by check or ACH?{' '}
                  <span className="font-semibold text-brix-primary underline">Click here</span>
                </button>
              ) : (
                <OtherWaysToPay onManualBank={() => setMode('bank_manual')} />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The two alternatives, revealed by the small line at the bottom of the form.
 *
 * ⚠ "Send payment myself" leads because it needs nothing saved and nothing
 * verified — for a customer who won't connect a bank login, this is usually
 * the answer rather than the fallback, and the microdeposit route costs them
 * 1-2 days for a code hunt they will not enjoy. That cost is stated BEFORE
 * they pick it, not on the next screen.
 */
function OtherWaysToPay({ onManualBank }: { onManualBank: () => void }) {
  return (
    <div className="space-y-3">
      <p className="text-[15px] leading-relaxed text-slate-700">
        Rather not have us charge you? You can pay us yourself, or give us your bank details to
        debit.
      </p>

      <Link
        to="/pay?selfpay=1"
        className="block rounded-2xl border-2 border-brix-primary/70 bg-brix-primary-tint/25 p-4 hover:bg-brix-primary-tint/50 transition-colors"
      >
        <span className="flex items-center gap-2">
          <ArrowRight size={18} className="text-brix-primary shrink-0" />
          <span className="text-base font-bold text-slate-900">Send payment myself</span>
        </span>
        <span className="mt-1 block text-sm leading-relaxed text-slate-700">
          Bank bill-pay, your own ACH, or a check. Nothing to save, nothing to verify, and you can
          do it today — our bank details are on the Pay page.
        </span>
      </Link>

      <button
        type="button"
        onClick={onManualBank}
        className="w-full text-left rounded-2xl border border-brix-border bg-brix-surface p-4 hover:border-brix-primary hover:bg-brix-primary-tint/40 transition-colors"
      >
        <span className="flex items-center gap-2">
          <Banknote size={18} className="text-brix-primary shrink-0" />
          <span className="text-base font-bold text-slate-900">Type in my bank details (ACH)</span>
        </span>
        <span className="mt-1 block text-sm text-brix-muted leading-relaxed">
          No bank login — just your routing and account number. The bank rules for debiting an
          account mean it has to be confirmed first: your bank shows a 1¢ deposit with a 6-digit
          code in 1–2 business days, and you enter that code. We can&rsquo;t charge it until then.
        </span>
      </button>
    </div>
  );
}

/**
 * The microdeposit hand-off. Stripe emails the customer a descriptor code and
 * hosts the page that takes it, so we send them there rather than rebuilding a
 * code-entry form — Stripe handles both microdeposit shapes, the retry limits
 * and the error copy, and it is the same page their email points at.
 */
function MicrodepositNext({ pending, onClose }: { pending: PendingResult; onClose: () => void }) {
  const arrive = pending.arriveOn
    ? new Date(`${pending.arriveOn}T00:00:00Z`).toLocaleDateString(undefined, {
        timeZone: 'UTC',
        month: 'long',
        day: 'numeric',
      })
    : null;

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5">
        <p className="flex items-center gap-2 text-base font-bold text-amber-900">
          <Clock size={17} /> Your bank is saved, but not confirmed yet
        </p>
        <p className="mt-1.5 text-[15px] leading-relaxed text-amber-900">
          We've asked your bank to accept a small test deposit
          {arrive ? <> — it should land by <span className="font-bold">{arrive}</span></> : ' (1–2 business days)'}.
          It arrives with a short code in the description.
        </p>
      </div>

      <p className="text-[15px] leading-relaxed text-slate-700">
        Stripe will email you when it's there. Enter the code on their secure page and this bank
        becomes usable — until then we can't charge it, so nothing will be taken.
      </p>

      {pending.verificationUrl && (
        <a
          href={pending.verificationUrl}
          target="_blank"
          rel="noreferrer"
          className="block w-full rounded-xl bg-brix-primary px-4 py-3 text-center text-base font-bold text-white hover:bg-brix-primary-deep transition-colors"
        >
          Enter the code →
        </a>
      )}

      <p className="text-sm text-brix-muted leading-relaxed">
        You can come back to this from{' '}
        <span className="font-semibold">Settings → Payment methods</span> any time — no need to
        keep this page open.
      </p>

      <button
        type="button"
        onClick={onClose}
        className="w-full rounded-xl border border-brix-border px-4 py-3 text-base font-semibold text-slate-700 hover:bg-brix-surface-alt/60"
      >
        Done
      </button>
    </div>
  );
}

function SetupForm({
  mode,
  onSaved,
  onPending,
  onBack,
  backLabel,
}: {
  mode: Mode;
  onSaved: (stripePmId: string) => void;
  onPending: (p: PendingResult) => void;
  onBack: () => void;
  backLabel: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [agreed, setAgreed] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!stripe || !elements || !agreed) return;
    setSubmitting(true);
    setError(null);

    const { error: confirmError, setupIntent } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      // Only redirect for methods that require it (most cards don't).
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message ?? 'Could not save the payment method.');
      setSubmitting(false);
      return;
    }

    // `requires_action` is the NORMAL outcome of a typed-in bank: Stripe is
    // waiting on the microdeposit. The server decides which it was — it reads
    // the SetupIntent itself rather than trusting the browser's copy.
    const usable =
      setupIntent &&
      (setupIntent.status === 'succeeded' || setupIntent.status === 'requires_action');
    if (!usable) {
      setError('The payment method could not be confirmed. Please try again.');
      setSubmitting(false);
      return;
    }

    try {
      const saved = await adminFetch<SaveResp>('stripe-save-method', {
        method: 'POST',
        body: JSON.stringify({
          setup_intent_id: setupIntent.id,
          authorization_version: PAYMENT_AUTHORIZATION_VERSION,
        }),
      });
      if (saved.status === 'pending_verification') {
        onPending({
          verificationUrl: saved.verification_url,
          arriveOn: saved.microdeposits_arrive_on,
        });
        return;
      }
      onSaved(saved.method.stripe_pm_id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Saved at the processor but could not record it — please refresh.',
      );
      setSubmitting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {mode === 'bank_manual' && (
        <p className="rounded-xl border border-brix-border bg-brix-surface-alt/50 px-4 py-3 text-[15px] leading-relaxed text-slate-700">
          Enter the routing and account number from a check or your bank's account details. We'll
          confirm the account with a small test deposit — <span className="font-semibold">1–2
          business days</span> — before it can be used.
        </p>
      )}

      <PaymentElement />

      {/* Payment authorization — visible on the customer side, required. */}
      <div className="rounded-xl border border-brix-border bg-brix-surface-alt/50 p-4 space-y-2">
        <p className="text-base font-bold text-slate-900">Payment authorization</p>
        <p className="text-sm text-brix-muted leading-relaxed">{PAYMENT_AUTHORIZATION_INTRO}</p>
        <ul className="list-disc pl-5 space-y-1 text-sm text-slate-700 leading-relaxed">
          {PAYMENT_AUTHORIZATION_CLAUSES.map((c) => (
            <li key={c}>{c}</li>
          ))}
        </ul>
        <label className="flex items-start gap-2.5 pt-1 cursor-pointer">
          <input
            type="checkbox"
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            className="mt-0.5 h-5 w-5 shrink-0 accent-brix-primary"
          />
          <span className="text-sm font-medium text-slate-900 leading-relaxed">{PAYMENT_AUTHORIZATION_ACK}</span>
        </label>
      </div>

      {error && (
        <p className="text-base text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
      )}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="px-4 py-3 rounded-xl border border-brix-border text-base font-semibold text-slate-700 hover:bg-brix-surface-alt/60 disabled:opacity-40"
        >
          {backLabel}
        </button>
        <button
          type="submit"
          disabled={!stripe || submitting || !agreed}
          className="flex-1 px-4 py-3 rounded-xl bg-brix-primary text-white text-base font-bold hover:bg-brix-primary-deep transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          {submitting ? <Loader2 size={17} className="animate-spin" /> : <Lock size={17} />}
          Save payment method
        </button>
      </div>
    </form>
  );
}
